/**
 * TokenPicker - Token-aware style controls for Element Inspector
 * 
 * Provides UI controls that are bound to design tokens:
 * - ColorTokenPicker: Dropdown swatches from tokens.colors
 * - FontFamilyPicker: Dropdown for token font families
 * - FontSizePicker: Dropdown for token font sizes
 * - FontWeightPicker: Dropdown for token font weights
 * - SpacingPicker: Dropdown for token spacing values
 * - RadiusPicker: Dropdown for token radius values
 * 
 * Each picker supports:
 * - "Use Token" mode (default) - value from tokens
 * - "Override" mode - custom raw value
 * - Reset to default button
 * - Override indicator dot
 */
import React, { useState } from 'react';
import { RotateCcw, ChevronDown, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';

// Override indicator dot
const OverrideIndicator = ({ level }) => {
  if (!level) return null;
  
  const colors = {
    element: 'bg-orange-500',
    block: 'bg-blue-500',
    page: 'bg-purple-500'
  };
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`w-2 h-2 rounded-full ${colors[level] || 'bg-zinc-500'}`} />
      </TooltipTrigger>
      <TooltipContent side="left">
        <p className="text-xs">Overridden at {level} level</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Generic token picker wrapper with override support
const TokenPickerWrapper = ({
  label,
  value,
  tokenValue,
  useToken,
  overrideLevel,
  onUseTokenChange,
  onValueChange,
  onReset,
  children,
  showAdvanced = false
}) => {
  const [showOverride, setShowOverride] = useState(!useToken);
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-zinc-300 text-sm">{label}</Label>
          <OverrideIndicator level={overrideLevel} />
        </div>
        <div className="flex items-center gap-1">
          {onReset && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onReset}
                  className="h-6 w-6 p-0 text-zinc-500 hover:text-white"
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset to default</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      
      {children}
      
      {showAdvanced && (
        <div className="flex items-center gap-2 pt-1">
          <Switch
            checked={showOverride}
            onCheckedChange={(checked) => {
              setShowOverride(checked);
              onUseTokenChange?.(!checked);
            }}
            className="scale-75"
          />
          <span className="text-[10px] text-zinc-500">Custom override</span>
        </div>
      )}
    </div>
  );
};

// Color Token Picker with swatches
export const ColorTokenPicker = ({
  label = 'Color',
  value,
  tokens,
  tokenPath, // e.g., 'colors.primary'
  overrideLevel,
  onChange,
  onReset
}) => {
  const colorTokens = tokens?.colors || {};
  const [showCustom, setShowCustom] = useState(false);
  
  // Get current value from token or override
  const currentColor = value?._override || (value?._tokenRef && tokens?.colors?.[value._tokenRef.split('.')[1]]) || value || colorTokens.text;
  
  return (
    <TokenPickerWrapper
      label={label}
      overrideLevel={overrideLevel}
      onReset={onReset}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button className="w-full flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-left hover:border-zinc-600 transition-colors">
            <span 
              className="w-5 h-5 rounded border border-zinc-600 flex-shrink-0"
              style={{ backgroundColor: currentColor }}
            />
            <span className="text-sm text-white flex-1 truncate">
              {value?._tokenRef?.split('.')[1] || 'Custom'}
            </span>
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 bg-zinc-900 border-zinc-700 p-3" align="start">
          <div className="space-y-3">
            <p className="text-xs text-zinc-400 font-medium">Token Colors</p>
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(colorTokens).map(([name, color]) => (
                <Tooltip key={name}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onChange({ _useToken: true, _tokenRef: `colors.${name}` })}
                      className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                        value?._tokenRef === `colors.${name}` 
                          ? 'border-orange-500 ring-2 ring-orange-500/30' 
                          : 'border-zinc-700 hover:border-zinc-500'
                      }`}
                      style={{ backgroundColor: color }}
                    >
                      {value?._tokenRef === `colors.${name}` && (
                        <Check className="w-4 h-4 text-white mx-auto drop-shadow-md" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{name}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
            
            <div className="border-t border-zinc-800 pt-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={showCustom}
                  onCheckedChange={setShowCustom}
                  className="scale-75"
                />
                <span className="text-xs text-zinc-400">Custom color</span>
              </div>
              {showCustom && (
                <Input
                  type="text"
                  value={value?._override || currentColor}
                  onChange={(e) => onChange({ _useToken: false, _override: e.target.value })}
                  placeholder="#ffffff"
                  className="mt-2 bg-zinc-800 border-zinc-700 text-white h-8 text-sm"
                />
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </TokenPickerWrapper>
  );
};

// Font Family Picker
export const FontFamilyPicker = ({
  label = 'Font Family',
  value,
  tokens,
  overrideLevel,
  onChange,
  onReset
}) => {
  const fontFamilies = tokens?.typography?.fontFamilies || {};
  const currentRef = value?._tokenRef?.split('.').pop() || 'body';
  
  return (
    <TokenPickerWrapper
      label={label}
      overrideLevel={overrideLevel}
      onReset={onReset}
    >
      <Select
        value={currentRef}
        onValueChange={(val) => onChange({ _useToken: true, _tokenRef: `typography.fontFamilies.${val}` })}
      >
        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          {Object.entries(fontFamilies).map(([name, family]) => (
            <SelectItem key={name} value={name} className="text-white">
              <span style={{ fontFamily: family }}>{name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TokenPickerWrapper>
  );
};

// Font Size Picker
export const FontSizePicker = ({
  label = 'Font Size',
  value,
  tokens,
  overrideLevel,
  onChange,
  onReset
}) => {
  const fontSizes = tokens?.typography?.fontSizes || {};
  const currentRef = value?._tokenRef?.split('.').pop() || 'base';
  
  return (
    <TokenPickerWrapper
      label={label}
      overrideLevel={overrideLevel}
      onReset={onReset}
    >
      <Select
        value={currentRef}
        onValueChange={(val) => onChange({ _useToken: true, _tokenRef: `typography.fontSizes.${val}` })}
      >
        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          {Object.entries(fontSizes).map(([name, size]) => (
            <SelectItem key={name} value={name} className="text-white">
              <span className="flex items-center justify-between w-full">
                <span>{name}</span>
                <span className="text-zinc-500 text-xs ml-2">{size}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TokenPickerWrapper>
  );
};

// Font Weight Picker
export const FontWeightPicker = ({
  label = 'Font Weight',
  value,
  tokens,
  overrideLevel,
  onChange,
  onReset
}) => {
  const fontWeights = tokens?.typography?.fontWeights || {};
  const currentRef = value?._tokenRef?.split('.').pop() || 'normal';
  
  return (
    <TokenPickerWrapper
      label={label}
      overrideLevel={overrideLevel}
      onReset={onReset}
    >
      <Select
        value={currentRef}
        onValueChange={(val) => onChange({ _useToken: true, _tokenRef: `typography.fontWeights.${val}` })}
      >
        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          {Object.entries(fontWeights).map(([name, weight]) => (
            <SelectItem key={name} value={name} className="text-white">
              <span style={{ fontWeight: weight }}>{name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TokenPickerWrapper>
  );
};

// Spacing Picker
export const SpacingPicker = ({
  label = 'Spacing',
  value,
  tokens,
  overrideLevel,
  onChange,
  onReset
}) => {
  const spacingTokens = tokens?.spacing || {};
  const currentRef = value?._tokenRef?.split('.').pop() || '4';
  
  return (
    <TokenPickerWrapper
      label={label}
      overrideLevel={overrideLevel}
      onReset={onReset}
    >
      <Select
        value={currentRef}
        onValueChange={(val) => onChange({ _useToken: true, _tokenRef: `spacing.${val}` })}
      >
        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700 max-h-64">
          {Object.entries(spacingTokens).map(([name, size]) => (
            <SelectItem key={name} value={name} className="text-white">
              <span className="flex items-center justify-between w-full">
                <span>{name}</span>
                <span className="text-zinc-500 text-xs ml-2">{size}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TokenPickerWrapper>
  );
};

// Radius Picker
export const RadiusPicker = ({
  label = 'Border Radius',
  value,
  tokens,
  overrideLevel,
  onChange,
  onReset
}) => {
  const radiusTokens = tokens?.radius || {};
  const currentRef = value?._tokenRef?.split('.').pop() || 'md';
  
  return (
    <TokenPickerWrapper
      label={label}
      overrideLevel={overrideLevel}
      onReset={onReset}
    >
      <div className="flex gap-1 bg-zinc-800/50 p-1 rounded-lg">
        {Object.entries(radiusTokens).map(([name, radius]) => (
          <Tooltip key={name}>
            <TooltipTrigger asChild>
              <button
                onClick={() => onChange({ _useToken: true, _tokenRef: `radius.${name}` })}
                className={`flex-1 p-2 rounded-md transition-colors ${
                  currentRef === name
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                }`}
              >
                <div 
                  className="w-4 h-4 mx-auto border border-current"
                  style={{ borderRadius: radius }}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{name} ({radius})</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TokenPickerWrapper>
  );
};

// Shadow Picker
export const ShadowPicker = ({
  label = 'Shadow',
  value,
  tokens,
  overrideLevel,
  onChange,
  onReset
}) => {
  const shadowTokens = tokens?.shadows || {};
  const currentRef = value?._tokenRef?.split('.').pop() || 'none';
  
  return (
    <TokenPickerWrapper
      label={label}
      overrideLevel={overrideLevel}
      onReset={onReset}
    >
      <Select
        value={currentRef}
        onValueChange={(val) => onChange({ _useToken: true, _tokenRef: `shadows.${val}` })}
      >
        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          {Object.entries(shadowTokens).map(([name]) => (
            <SelectItem key={name} value={name} className="text-white capitalize">
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TokenPickerWrapper>
  );
};

// Export all pickers
export default {
  ColorTokenPicker,
  FontFamilyPicker,
  FontSizePicker,
  FontWeightPicker,
  SpacingPicker,
  RadiusPicker,
  ShadowPicker
};
